import type {
  FiscalDocumentStatus,
  FiscalEventKind,
  FiscalEventStatus,
  FiscalEventView,
} from "@cx/contracts";
import type { Tone } from "@cx/ui";

/** Rótulos da NF-e de saída (T2.3). */

export const INVOICE_STATUS_LABEL: Record<FiscalDocumentStatus, string> = {
  draft: "Rascunho",
  queued: "Na fila de envio",
  sending: "Enviando à SEFAZ",
  authorized: "Autorizada",
  rejected: "Rejeitada",
  denied: "Denegada",
  canceled: "Cancelada",
  voided: "Inutilizada",
};

export const INVOICE_STATUS_TONE: Record<FiscalDocumentStatus, Tone> = {
  draft: "secondary",
  queued: "info",
  sending: "info",
  authorized: "success",
  rejected: "danger",
  denied: "danger",
  canceled: "secondary",
  voided: "secondary",
};

export const ENVIRONMENT_LABEL: Record<1 | 2, string> = { 1: "Produção", 2: "Homologação" };

/** Situações que ainda mudam sozinhas (a tela consulta de novo). */
export const isInFlight = (s: FiscalDocumentStatus) => s === "queued" || s === "sending";

/** Chave de acesso em grupos de 4 (como no DANFE). */
export const accessKeyGroups = (k: string) => k.replace(/(.{4})/g, "$1 ").trim();

/** "NF-e nº 12 (série 1)"; rascunho sem número. */
export const invoiceTitle = (d: { number: number | null; series: number | null }) =>
  d.number === null ? "NF-e (rascunho)" : `NF-e nº ${d.number} (série ${d.series ?? 1})`;

/** Mensagem da SEFAZ/provedor: "778 — Rejeição: …". */
export const sefazMessage = (code: string | null, reason: string | null) =>
  reason ? (code && code !== "provider" ? `${code} — ${reason}` : reason) : null;

// ===== Eventos da NF-e (T2.4) =====

export const EVENT_KIND_LABEL: Record<FiscalEventKind, string> = {
  cancel: "Cancelamento",
  correction: "Carta de correção",
  void_range: "Inutilização",
};

export const EVENT_STATUS_LABEL: Record<FiscalEventStatus, string> = {
  pending: "Na fila de envio",
  sending: "Enviando à SEFAZ",
  registered: "Registrado",
  rejected: "Recusado",
};

export const EVENT_STATUS_TONE: Record<FiscalEventStatus, Tone> = {
  pending: "info",
  sending: "info",
  registered: "success",
  rejected: "danger",
};

/** Evento que ainda muda sozinho (a tela consulta de novo). */
export const eventInFlight = (e: Pick<FiscalEventView, "status">) =>
  e.status === "pending" || e.status === "sending";

/** "Carta de correção nº 2", "Cancelamento", "Inutilização dos números 3 a 5 (série 1)". */
export const eventTitle = (
  e: Pick<FiscalEventView, "kind" | "sequence" | "voidSeries" | "voidFrom" | "voidTo">,
) =>
  e.kind === "correction"
    ? `Carta de correção nº ${e.sequence}`
    : e.kind === "void_range"
      ? e.voidFrom === e.voidTo
        ? `Inutilização do número ${e.voidFrom} (série ${e.voidSeries})`
        : `Inutilização dos números ${e.voidFrom} a ${e.voidTo} (série ${e.voidSeries})`
      : "Cancelamento";
